import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { track } from "@/lib/analytics";

const STRIPS = [
  {
    area: "Marca empleadora",
    before: "No sabéis por qué ",
    mark: "cuesta tanto atraer talento",
    after: ", o por qué los candidatos buenos acaban eligiendo otra oferta.",
  },
  {
    area: "Selección y contratación",
    before: "Contratáis sobre todo ",
    mark: "por intuición",
    after: ", y el desajuste se descubre meses después, cuando ya ha salido caro.",
  },
  {
    area: "Onboarding",
    before: "Cada incorporación depende de ",
    mark: "quién tenga tiempo esa semana",
    after: " para explicarle cómo funciona todo.",
  },
  {
    area: "Evaluación del desempeño",
    before: "Nadie sabe con certeza cómo le está yendo, hasta que ",
    mark: "algo se rompe",
    after: " y ya es tarde para corregirlo.",
  },
  {
    area: "Feedback y comunicación interna",
    before: "Las decisiones y el contexto se quedan en ",
    mark: "la cabeza del fundador",
    after: ", y el equipo se entera a medias.",
  },
  {
    area: "Desarrollo profesional y retención",
    before: "La gente que mejor funciona se va porque ",
    mark: "no ve un camino claro",
    after: " de crecimiento dentro de la empresa.",
  },
];

const TAB_LABELS = ["Marca empleadora", "Selección y contratación", "Onboarding", "Desempeño", "Feedback", "Retención"];

const pageTurn = {
  enter: (dir) => ({
    opacity: 0,
    rotateY: dir > 0 ? 65 : -65,
    transformOrigin: dir > 0 ? "left center" : "right center",
  }),
  center: { opacity: 1, rotateY: 0 },
  exit: (dir) => ({
    opacity: 0,
    rotateY: dir > 0 ? -65 : 65,
    transformOrigin: dir > 0 ? "left center" : "right center",
  }),
};

export default function Problems() {
  const [problemIdx, setProblemIdx] = useState(0);
  const [turnDir, setTurnDir] = useState(1);
  const current = STRIPS[problemIdx];
  const goNext = () => {
    track("problem_carousel_advance");
    setTurnDir(1);
    setProblemIdx((i) => (i + 1) % STRIPS.length);
  };
  const goPrev = (e) => {
    e?.stopPropagation?.();
    setTurnDir(-1);
    setProblemIdx((i) => (i - 1 + STRIPS.length) % STRIPS.length);
  };
  const goTo = (i, e) => {
    e?.stopPropagation?.();
    if (i === problemIdx) return;
    track("problem_carousel_jump");
    setTurnDir(i > problemIdx ? 1 : -1);
    setProblemIdx(i);
  };

  return (
    <section
      id="problemas"
      data-testid="problems-section"
      className="relative bg-[#F7F9F8] text-[#06231D] py-28 lg:py-40 grain"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-16 lg:mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 lg:items-start">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-4">
              <span className="font-serif-italic text-[#085041] text-[9rem] lg:text-[12rem] leading-none -mt-5 lg:-mt-8 block">01</span>
              <span className="inline-block text-xs lg:text-sm uppercase tracking-[0.18em] font-medium text-[#F7F9F8] bg-[#06231D] px-3 py-2 rounded-md">El problema</span>
            </div>
          </div>

          <div className="lg:col-span-8 space-y-8">
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">
              <span className="font-serif-italic text-[#085041] font-normal">Si alguna de estas frases te suena</span>, ya sabes por dónde empezar.
            </h2>
            <p className="text-[#06231D]/70 text-lg leading-relaxed max-w-2xl font-light text-justify hyphens-auto">
              La gestión del talento falla casi siempre en silencio, hasta que el síntoma se vuelve demasiado caro para ignorarlo.
            </p>
          </div>
        </div>
      </div>

      {/* Carrusel: una tarjeta esmeralda premium, un problema por slide */}
      <div className="max-w-[1020px] mx-auto px-6 lg:px-10">
        <div className="relative">
          {/* Flechas laterales (escritorio) */}
          <button
            onClick={goPrev}
            aria-label="Problema anterior"
            data-testid="problem-carousel-prev"
            className="hidden lg:grid absolute -left-7 top-1/2 -translate-y-1/2 z-20 w-[52px] h-[52px] rounded-full border border-[#C9A84C]/50 bg-[#06231D]/85 text-[#E9CE8C] place-items-center hover:bg-[#C9A84C] hover:text-[#06231D] transition-colors backdrop-blur"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={goNext}
            aria-label="Siguiente problema"
            data-testid="problem-carousel-next"
            className="hidden lg:grid absolute -right-7 top-1/2 -translate-y-1/2 z-20 w-[52px] h-[52px] rounded-full border border-[#C9A84C]/50 bg-[#06231D]/85 text-[#E9CE8C] place-items-center hover:bg-[#C9A84C] hover:text-[#06231D] transition-colors backdrop-blur"
          >
            <ChevronRight size={20} />
          </button>

          {/* Marco dorado */}
          <div className="p-[2px] rounded-[30px] bg-[linear-gradient(120deg,#C9A84C,#F0DCA0_45%,#A8853C)] shadow-[0_34px_80px_-28px_rgba(168,133,60,0.55)]">
            <div
              role="button"
              tabIndex={0}
              onClick={goNext}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  goNext();
                }
              }}
              data-testid="problem-carousel"
              aria-label="Ver el siguiente problema"
              className="relative overflow-hidden cursor-pointer rounded-[28px] bg-[linear-gradient(160deg,#0B5540,#0E6B4D_60%,#127A55)] px-7 sm:px-12 lg:px-16 pt-9 lg:pt-12 min-h-[320px] md:min-h-[380px] flex flex-col select-none [perspective:1400px]"
            >
              <AnimatePresence mode="wait" custom={turnDir}>
                <motion.div
                  key={problemIdx}
                  custom={turnDir}
                  variants={pageTurn}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  style={{ transformStyle: "preserve-3d", backfaceVisibility: "hidden" }}
                  className="relative flex flex-col gap-4 lg:gap-5 flex-1"
                >
                  <span
                    aria-hidden
                    className="absolute right-0 lg:right-2 -top-2 font-serif-italic text-[84px] lg:text-[120px] leading-none text-[#E9CE8C]/25 pointer-events-none"
                  >
                    0{problemIdx + 1}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.24em] text-[#C9A84C] font-semibold" data-testid="problem-carousel-counter">
                    Problema 0{problemIdx + 1} / 06
                  </span>
                  <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#8FEAC3] [text-shadow:0_0_14px_rgba(46,196,138,0.45)] pr-16 lg:pr-24" data-testid="problem-carousel-area">
                    {current.area}
                  </span>
                  <p className="font-display text-xl sm:text-2xl lg:text-[27px] leading-snug tracking-tight text-[#F7F9F8] max-w-[34ch]">
                    {current.before}
                    <span className="font-semibold">{current.mark}</span>
                    {current.after}
                  </p>
                  <div className="h-5 lg:h-8" />
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#C9A84C]/85 pb-5">
                    Ver el siguiente problema
                  </span>
                </motion.div>
              </AnimatePresence>

              {/* Fichas numeradas: salto directo a cada problema */}
              <div className="relative z-10 grid grid-cols-6 gap-1.5 lg:gap-2.5 pb-5 -mt-1">
                {STRIPS.map((s, i) => (
                  <button
                    key={s.area}
                    type="button"
                    onClick={(e) => goTo(i, e)}
                    data-testid={`problem-tab-${i}`}
                    aria-label={`Ir al problema ${i + 1}: ${s.area}`}
                    className={`relative flex items-center justify-center rounded-xl border px-2 py-2.5 lg:px-3 lg:py-3.5 transition-colors cursor-pointer ${
                      i === problemIdx
                        ? "border-[#C9A84C]/70 bg-[#C9A84C]/[0.12]"
                        : "border-white/15 bg-[#06231D]/25 hover:border-white/30"
                    }`}
                  >
                    <span className={`font-serif-italic text-sm lg:text-[13px] leading-none lg:absolute lg:top-1.5 lg:right-2 ${i === problemIdx ? "text-[#E9CE8C] font-bold" : "text-[#E9CE8C]/60"}`}>
                      0{i + 1}
                    </span>
                    <span className={`hidden lg:block text-[9.5px] uppercase tracking-[0.1em] leading-tight text-center ${i === problemIdx ? "text-[#F4E5BD]" : "text-[#F7F9F8]/45"}`}>
                      {TAB_LABELS[i]}
                    </span>
                  </button>
                ))}
              </div>

              {/* Barra de progreso */}
              <div className="relative -mx-7 sm:-mx-12 lg:-mx-16 h-[5px] bg-[#06231D]/35">
                <div
                  data-testid="problem-carousel-progress"
                  className="h-full bg-[linear-gradient(90deg,#C9A84C,#E9CE8C)] transition-all duration-500"
                  style={{ width: `${((problemIdx + 1) / STRIPS.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-16 lg:mt-24 text-center">
        <p
          data-testid="problems-closing-line"
          className="font-serif-italic text-2xl lg:text-[clamp(26px,2.4vw,36px)] text-[#06231D] leading-snug max-w-none lg:whitespace-nowrap mx-auto"
        >
          Si alguna te suena, <span className="font-bold">ya no es una corazonada</span>. Es un <span className="font-bold">patrón</span>, y los patrones se pueden medir.
        </p>
      </div>
    </section>
  );
}
